import { useId, useState } from 'react'

import { Button } from './Button'
import './pickList.css'

export interface PickListItem {
  id: string
  label: string
  /** Вторая строка мелким шрифтом: источник, пометка, статус. */
  note?: string | null
  disabled?: boolean
}

export interface PickListProps {
  items: readonly PickListItem[]
  /** Выбранная строка подсвечивается и объявляется как нажатая. */
  selectedId?: string
  onPick: (id: string) => void
  /** Подпись поиска и имя списка для диктора: «Найти кассира» / «Кассиры источника». */
  searchLabel: string
  listLabel: string
  /** Поиск живёт внутри; снаружи его передают, только если запрос нужен экрану (`query` и `onQuery` вместе). */
  query?: string
  onQuery?: (value: string) => void
  /** Список ещё читается. */
  busy?: boolean
  /** Что сказать, когда в источнике никого нет и когда ничего не нашлось по запросу. */
  emptyText: string
  notFoundText: string
  /** Кнопка «Обновить» на всю ширину под списком: единое место обновления для любого списка. Нет обработчика — нет кнопки. */
  onReload?: () => void
  /** Подпись кнопки обновления (по умолчанию «Обновить») и запись реестра действий для ⓘ. */
  reloadLabel?: string
  reloadActionId?: string
  /** Идёт перечитывание: кнопка обновления выключена и объявляет это диктору. */
  reloadBusy?: boolean
}

/**
 * Поиск и список для выбора одного из многих (кассир, магазин, человек): компактная строка поиска и строки одной высоты
 * без кнопок «ⓘ» — строка и так понятна. Один компонент на все такие выборы, чтобы список везде выглядел одинаково.
 */
export function PickList(props: PickListProps) {
  const [inner, setInner] = useState('')
  const query = props.query ?? inner
  const setQuery = props.onQuery ?? setInner
  const id = useId()
  const needle = query.trim().toLocaleLowerCase('ru-RU')
  const visible = props.items.filter((item) => item.label.toLocaleLowerCase('ru-RU').includes(needle))
  return (
    <div className="krp-picklist">
      <input
        type="search"
        className="krp-picklist__search"
        aria-label={props.searchLabel}
        placeholder={props.searchLabel}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <ul className="krp-picklist__list" role="group" aria-label={props.listLabel} aria-busy={props.busy === true} id={id}>
        {visible.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className="krp-picklist__row"
              aria-pressed={props.selectedId === undefined ? undefined : props.selectedId === item.id}
              disabled={item.disabled === true}
              onClick={() => props.onPick(item.id)}
            >
              <span className="krp-picklist__name">{item.label}</span>
              {item.note != null && item.note !== '' && <small className="krp-picklist__note">{item.note}</small>}
            </button>
          </li>
        ))}
      </ul>
      {props.busy === true && <p className="krp-picklist__status" role="status">Загружаем список…</p>}
      {props.busy !== true && visible.length === 0 && (
        <p className="krp-picklist__status" role="status">{needle === '' ? props.emptyText : props.notFoundText}</p>
      )}
      {props.onReload !== undefined && (
        <div className="krp-picklist__reload">
          <Button variant="ghost" busy={props.reloadBusy === true}
            {...(props.reloadActionId !== undefined ? { actionId: props.reloadActionId } : {})} onClick={props.onReload}>
            {props.reloadLabel ?? 'Обновить'}
          </Button>
        </div>
      )}
    </div>
  )
}
